import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Calendar, Clock, MapPin, AlertCircle, CheckCircle, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';

const API = process.env.REACT_APP_BACKEND_URL;

const ReschedulePage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newDate, setNewDate] = useState('');
  const [newTime, setNewTime] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const availableTimes = [
    "9:00 AM", "10:00 AM", "11:00 AM", "12:00 PM",
    "1:00 PM", "2:00 PM", "3:00 PM", "4:00 PM"
  ];

  useEffect(() => {
    const fetchBooking = async () => {
      if (!token) {
        setError('Invalid reschedule link. Please use the link from your confirmation email.');
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${API}/api/bookings/reschedule/${token}`);
        setBooking(response.data.booking);
      } catch (err) {
        setError(err.response?.data?.detail || 'Unable to find your booking. The link may be invalid or expired.');
      } finally {
        setLoading(false);
      }
    };

    fetchBooking();
  }, [token]);

  const handleReschedule = async (e) => {
    e.preventDefault();
    
    if (!newDate) {
      toast.error('Please select a new pickup date');
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/api/bookings/${booking.id}/reschedule`, {
        pickupDate: newDate,
        pickupTime: newTime || booking.pickupTime,
        token: token
      });

      setSuccess(true);
      toast.success('Booking rescheduled successfully!');
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to reschedule. Please try again or contact us.';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get minimum date (tomorrow)
  const getMinDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your booking...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">Oops!</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <Link to="/">
              <Button className="bg-teal-600 hover:bg-teal-700">
                <ArrowLeft size={16} className="mr-2" />
                Back to Home
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">Rescheduled!</h2>
            <p className="text-gray-600 mb-2">
              Your pickup has been rescheduled to:
            </p>
            <p className="text-2xl font-bold text-teal-600 mb-1">{newDate}</p>
            <p className="text-lg text-gray-700 mb-6">{newTime || booking.pickupTime}</p>
            <p className="text-sm text-gray-500 mb-6">
              We'll contact you to confirm the pickup time.
            </p>
            <Link to="/">
              <Button className="bg-teal-600 hover:bg-teal-700">
                <ArrowLeft size={16} className="mr-2" />
                Back to Home
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!booking.can_reschedule) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">Cannot Reschedule</h2>
            <p className="text-gray-600 mb-4">
              {booking.reschedule_count >= 1 
                ? "This booking has already been rescheduled once. Please contact us directly for any further changes."
                : "This booking cannot be rescheduled at this time. It may already be in progress."}
            </p>
            <div className="bg-gray-100 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-600">Need help? Contact us:</p>
              <p className="font-semibold text-teal-600">
                <a href="tel:762-436-2067">762-436-2067</a> | <a href="tel:404-424-2452">404-424-2452</a>
              </p>
            </div>
            <Link to="/">
              <Button className="bg-teal-600 hover:bg-teal-700">
                <ArrowLeft size={16} className="mr-2" />
                Back to Home
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-lg mx-auto">
        <Link to="/" className="inline-flex items-center text-teal-600 hover:text-teal-700 mb-6">
          <ArrowLeft size={20} className="mr-2" />
          Back to Home
        </Link>

        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">Reschedule Your Pickup</CardTitle>
          </CardHeader>
          <CardContent>
            {/* Current Booking Info */}
            <div className="bg-gray-100 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-gray-700 mb-3">Current Booking:</h3>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 text-gray-600">
                  <span className="font-medium">Name:</span> {booking.name}
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <Calendar size={16} />
                  <span className="font-medium">Date:</span> {booking.pickupDate}
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <Clock size={16} />
                  <span className="font-medium">Time:</span> {booking.pickupTime || 'TBD'}
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <MapPin size={16} />
                  <span className="font-medium">City:</span> {booking.city}
                </div>
              </div>
            </div>

            {/* Reschedule Form */}
            <form onSubmit={handleReschedule} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New Pickup Date *
                </label>
                <input
                  type="date"
                  value={newDate}
                  onChange={(e) => setNewDate(e.target.value)}
                  min={getMinDate()}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New Pickup Time (Optional)
                </label>
                <select
                  value={newTime}
                  onChange={(e) => setNewTime(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                >
                  <option value="">Keep current time ({booking.pickupTime || 'TBD'})</option>
                  {availableTimes.map((time) => (
                    <option key={time} value={time}>{time}</option>
                  ))}
                </select>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                <AlertCircle size={16} className="inline mr-2" />
                <strong>Note:</strong> You can only reschedule once. Make sure to choose the right date.
              </div>

              <Button
                type="submit"
                disabled={isSubmitting || !newDate}
                className="w-full bg-teal-600 hover:bg-teal-700 py-6 text-lg"
              >
                {isSubmitting ? 'Rescheduling...' : 'Confirm Reschedule'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ReschedulePage;
